import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Flag,
  Bot,
  MessageSquareText,
  Mic,
  ListChecks,
  CalendarDays,
  Smartphone,
  User,
  Users,
  ClipboardList,
  Wrench,
  Megaphone,
} from "lucide-react";

const FEATURES = [
  {
    icon: Bot,
    title: "Campaign Advisor",
    body: "An AI strategist on call for fundraising, field, messaging, and 'what do I do next' — grounded in your race, whether it's your first or your fifth.",
    tone: "red",
  },
  {
    icon: MessageSquareText,
    title: "Social Post Generator",
    body: "On-voice Facebook, X, and Instagram drafts in seconds. New to posting? We give you three ready-to-go drafts. Done it a thousand times? Skip the blank page and edit instead of write.",
    tone: "red",
  },
  {
    icon: Mic,
    title: "Speech Writer",
    body: "Full speeches or quick talking points for any event. First kickoff or fiftieth chicken dinner, you walk in with the right words already in your hand.",
    tone: "navy",
  },
  {
    icon: ListChecks,
    title: "Launch Checklist",
    body: "Every step of running for office, in order — from filing through election night. Useful for new candidates, a sanity check for seasoned ones.",
    tone: "red",
  },
  {
    icon: CalendarDays,
    title: "Campaign Calendar & Scheduler",
    body: "Plan events and content side by side. Upload a flyer and we pull out the details. First-time candidates get a head start; veteran staff get hours back every week.",
    tone: "red",
  },
  {
    icon: Smartphone,
    title: "Friendly to first-timers. Built for pros.",
    body: "Big buttons, plain language, mobile-first. Approachable enough that a school board candidate can run their whole race on their phone. Sharp enough that a veteran operative trusts it.",
    tone: "navy",
  },
];

const STEPS = [
  {
    icon: ClipboardList,
    title: "Tell us about your race",
    body: "Office, district, opponent, top issues, voice. Five-minute guided intake — once, and every tool is grounded in it.",
  },
  {
    icon: Wrench,
    title: "Get your tools and content",
    body: "Posts, speeches, a strategist on call, and a launch checklist that knows where you are in the cycle.",
  },
  {
    icon: Megaphone,
    title: "Run a sharper campaign",
    body: "Show up to every event with the right post drafted, the right talking points ready, and the next step on your list. First-timers feel prepared. Veterans skip the busywork.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-flag-ivory">
      <div className="stripe-accent" />
      <header className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display font-black text-xl text-flag-navy" data-testid="landing-logo">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-flag-red text-white">
            <Flag className="h-4 w-4" />
          </span>
          Electable
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="landing-login-link">
            <Button variant="ghost" className="text-flag-navy font-semibold">Log in</Button>
          </Link>
          <Link to="/register" data-testid="landing-register-link">
            <Button className="bg-flag-red hover:bg-flag-red-hover text-white font-bold h-11 px-5">
              Get started
            </Button>
          </Link>
        </div>
      </header>

      <main>
        {/* HERO */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-up">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-flag-red/10 text-flag-red text-xs font-semibold uppercase tracking-wider">
                <Sparkles className="h-3.5 w-3.5" /> Built by someone who&rsquo;s actually run campaigns
              </span>
              <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-flag-navy leading-[1.05]" data-testid="hero-headline">
                Run a campaign like you&rsquo;ve got a team behind you.
              </h1>
              <p className="mt-5 text-lg sm:text-xl text-slate-700 leading-relaxed max-w-xl" data-testid="hero-subheadline">
                Whether it&rsquo;s your first race or your fifth, Electable gives Republican candidates, committees, and staff the content, strategy, and structure to run a sharp campaign.
              </p>
              <div className="mt-5 grid sm:grid-cols-2 gap-3 max-w-xl" data-testid="hero-audience-grid">
                <div className="flex items-start gap-2.5 bg-white border border-slate-200 rounded-lg px-3.5 py-2.5">
                  <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-md bg-flag-red text-white text-xs font-black">1</span>
                  <p className="text-sm text-slate-700 leading-snug"><strong className="text-flag-navy">New to this?</strong> We&rsquo;ll guide you step by step.</p>
                </div>
                <div className="flex items-start gap-2.5 bg-white border border-slate-200 rounded-lg px-3.5 py-2.5">
                  <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-md bg-flag-navy text-white text-xs font-black">5+</span>
                  <p className="text-sm text-slate-700 leading-snug"><strong className="text-flag-navy">Done it before?</strong> We&rsquo;ll save you hours.</p>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register" data-testid="hero-cta-register">
                  <Button className="bg-flag-red hover:bg-flag-red-hover text-white font-bold h-12 px-6 text-base">
                    Start your campaign <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/login" data-testid="hero-cta-login">
                  <Button variant="outline" className="h-12 px-6 text-base font-bold border-2 border-slate-300 hover:border-flag-navy">
                    I already have an account
                  </Button>
                </Link>
              </div>
              <p className="mt-6 text-sm text-slate-500 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-flag-navy" /> Private to your account. Your campaign data is yours.
              </p>
            </div>
            <div className="relative animate-fade-up">
              <div className="absolute -inset-4 bg-stars rounded-2xl" aria-hidden="true" />
              <SocialPreview />
            </div>
          </div>
        </section>

        {/* THREE ACCOUNT TYPES */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="landing-roles-section">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-flag-red">Choose your path</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-black text-flag-navy leading-tight">Built for your role in the campaign.</h2>
            <p className="mt-3 text-slate-600 text-lg leading-relaxed">
              One platform, three accounts — each tailored to how you actually work. All share the same AI content and strategy engine, but the intake, prompts, and tools change based on whether you're the candidate, the committee, or campaign staff.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-5" data-testid="landing-roles-grid">
            <RoleCard
              icon={User}
              title="For Candidates"
              tagline="Run a focused, serious campaign on your terms."
              accent="red"
              bullets={[
                "Guided campaign setup and profile",
                "AI social posts written in your voice",
                "Speech Writer — full speeches and talking points",
                "Campaign Advisor — strategy whenever you need it",
                "Launch Checklist — from filing to election night",
                "Calendar and graphics tools",
              ]}
              ctaLabel="Start as a Candidate"
              ctaTo="/register?type=candidate"
              testid="landing-role-candidate"
            />
            <RoleCard
              icon={Users}
              title="For Committees"
              tagline="Run a sharper, more organized county operation."
              accent="navy"
              bullets={[
                "Committee content in your institutional voice",
                "County-wide messaging and event promotion",
                "Campaign Advisor — committee strategy",
                "Tools to coordinate and support your slate",
                "Calendar and Launch Checklist for the committee year",
              ]}
              ctaLabel="Start as a Committee"
              ctaTo="/register?type=committee"
              testid="landing-role-committee"
            />
            <RoleCard
              icon={Megaphone}
              title="For Campaign Staff"
              tagline="Run the operation, manage volunteers, execute the field plan."
              accent="red"
              bullets={[
                "Speech writing for candidates and surrogates",
                "Operations-focused Campaign Advisor",
                <><span className="font-semibold">Volunteer Database</span> (Beta)</>,
                <><span className="font-semibold">Lawn Sign Tracker</span> (Beta)</>,
                "Event and logistics management",
                "Content for the campaign's accounts",
              ]}
              ctaLabel="Start as a Staffer"
              ctaTo="/register?type=staffer"
              testid="landing-role-staffer"
            />
          </div>

          {/* Pricing block — Pro upgrade stated once here, out of the role
              cards. Finances Suite description expanded so it reads as a
              distinct filing-report tool, not "another money thing." */}
          <div className="mt-8 bg-white border border-slate-200 rounded-xl p-5 sm:p-6" data-testid="landing-pricing-strip">
            <p className="text-xs font-bold uppercase tracking-wider text-flag-red">Pricing — same for all three roles</p>
            <div className="mt-3 grid sm:grid-cols-2 gap-4">
              <div className="border border-slate-200 rounded-lg p-4" data-testid="landing-pricing-standard">
                <div className="font-display font-black text-2xl text-flag-navy">Standard <span className="text-flag-navy/70">— $34.99/mo</span></div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  Everything in your role&rsquo;s toolkit above.
                </p>
              </div>
              <div className="border-2 border-flag-red rounded-lg p-4 bg-flag-red/5" data-testid="landing-pricing-pro">
                <div className="font-display font-black text-2xl text-flag-navy">
                  Pro <span className="text-flag-navy/70">— $49.99/mo</span>
                  <span className="ml-2 align-middle inline-flex items-center text-[10px] font-bold uppercase tracking-wider bg-flag-red text-white px-2 py-0.5 rounded">Upgrade</span>
                </div>
                <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                  Everything in Standard, plus two power tools:
                </p>
                <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
                  <li>
                    <strong className="text-flag-navy">Voter List CRM</strong> — organize and work your voter contacts.
                  </li>
                  <li>
                    <strong className="text-flag-navy">Finances Suite</strong> — manually log donations and expenses, track your budget, and export clean CSVs to use when preparing your filing reports.
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
              <p className="text-sm text-slate-500">7-day free trial · $0 due today · cancel anytime before it ends and you're not charged.</p>
              <Link to="/register" data-testid="landing-pricing-cta">
                <Button className="bg-flag-red hover:bg-flag-red-hover text-white font-bold h-11 px-5">Start free <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
              </Link>
            </div>
          </div>
        </section>
        <section className="bg-slate-50 border-y border-slate-200 mt-4" data-testid="landing-features-section">
          <div className="stripe-accent" aria-hidden="true" />
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-flag-red">The whole toolkit</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-black text-flag-navy leading-tight">
              Everything a winning campaign needs.
            </h2>
            <p className="mt-3 text-slate-600 text-lg leading-relaxed">
              Each tool is described by what it gets you, not how it works. Powerful enough for a seasoned pro, simple enough for a first-time candidate.
            </p>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="landing-features-grid">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              const iconCls = f.tone === "red"
                ? "bg-flag-red text-white"
                : "bg-flag-navy text-white";
              return (
                <div
                  key={f.title}
                  data-testid={`landing-feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="bg-white rounded-xl border border-slate-200 p-6 sm:p-7 shadow-sm hover:border-flag-navy hover:shadow-md transition-all"
                >
                  <div className={`h-11 w-11 rounded-lg ${iconCls} flex items-center justify-center`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-bold text-flag-navy">{f.title}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed text-[15px]">{f.body}</p>
                </div>
              );
            })}
          </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="bg-flag-red/[0.04] border-b border-flag-red/15" data-testid="landing-how-it-works-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-flag-red">How it works</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-black text-flag-navy leading-tight">
              Set up once. Show up sharper every day after.
            </h2>
          </div>
          <ol className="mt-8 grid md:grid-cols-3 gap-5">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <li
                  key={s.title}
                  data-testid={`landing-step-${i + 1}`}
                  className="relative bg-white rounded-xl border border-slate-200 p-6 sm:p-7 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-flag-red text-white font-display font-black text-base">
                      {i + 1}
                    </span>
                    <Icon className="h-5 w-5 text-flag-red" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-bold text-flag-navy">{s.title}</h3>
                  <p className="mt-1.5 text-slate-600 leading-relaxed text-[15px]">{s.body}</p>
                </li>
              );
            })}
          </ol>
          </div>
        </section>

        {/* ACCOUNT TYPES */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="landing-account-types-section">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-10 shadow-sm">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-flag-red">Three ways in</p>
              <h2 className="mt-2 font-display text-2xl sm:text-3xl font-black text-flag-navy leading-tight">
                For Republican candidates. For Republican committees. For campaign staff.
              </h2>
            </div>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <div
                data-testid="landing-account-type-candidate"
                className="border border-slate-200 rounded-xl p-5 sm:p-6 hover:border-flag-red transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-flag-red/10 text-flag-red">
                    <User className="h-5 w-5" />
                  </span>
                  <h3 className="font-display text-lg font-bold text-flag-navy">Republican Candidates</h3>
                </div>
                <p className="mt-3 text-slate-600 leading-relaxed text-[15px]">
                  First-person voice grounded in your race &mdash; opponent and platform prep to review and build on, a launch checklist that walks you from filing through election night, and content sized for the office you&rsquo;re actually running for. Town council, county legislature, state house, and everything in between.
                </p>
              </div>
              <div
                data-testid="landing-account-type-committee"
                className="border border-slate-200 rounded-xl p-5 sm:p-6 hover:border-flag-navy transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-flag-navy/10 text-flag-navy">
                    <Users className="h-5 w-5" />
                  </span>
                  <h3 className="font-display text-lg font-bold text-flag-navy">Republican Committees</h3>
                </div>
                <p className="mt-3 text-slate-600 leading-relaxed text-[15px]">
                  Institutional &ldquo;we&rdquo; voice for your committee. Plan recurring events, draft countywide messaging, recruit volunteers, contrast against the other party, and power the candidates running on your line.
                </p>
              </div>
              <div
                data-testid="landing-account-type-staffer"
                className="border border-slate-200 rounded-xl p-5 sm:p-6 hover:border-flag-red transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-flag-red/10 text-flag-red">
                    <Megaphone className="h-5 w-5" />
                  </span>
                  <h3 className="font-display text-lg font-bold text-flag-navy">Campaign Staff</h3>
                </div>
                <p className="mt-3 text-slate-600 leading-relaxed text-[15px]">
                  Operations voice for the people actually running the race. Manage volunteers, track lawn-sign requests, write speeches and surrogate remarks, schedule the field plan, and keep the candidate&rsquo;s accounts moving day to day.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <div className="bg-flag-navy rounded-2xl px-6 py-12 sm:p-12 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-stars opacity-20" aria-hidden="true" />
            <div className="relative max-w-2xl">
              <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Republicans, this one&rsquo;s for you. Run the campaign your district deserves.</h2>
              <p className="mt-3 text-slate-200">Free to try. Set up takes about five minutes.</p>
              <Link to="/register" data-testid="footer-cta-register">
                <Button className="mt-6 bg-flag-red hover:bg-flag-red-hover text-white font-bold h-12 px-6 text-base">
                  Create my campaign <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-sm text-slate-500">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>© {new Date().getFullYear()} Electable LLC. Republican campaign tools for candidates, committees, and campaign staff at every level.</div>
            <div className="font-display font-bold text-flag-navy">Electable</div>
          </div>
          <div className="mt-3 text-xs text-slate-400" data-testid="landing-party-disclaimer">
            Electable LLC is an independent company. It is not affiliated with, endorsed by, or authorized by the Republican National Committee or any party committee.
          </div>
        </div>
      </footer>
    </div>
  );
}

function SocialPreview() {
  // Three generic sample drafts (endorsement / event / contrast). Each kept
  // to a similar short length so the card height doesn't jump as it rotates.
  const slides = [
    {
      channel: "Facebook",
      eyebrow: "Draft · Facebook",
      body: (
        <>
          Proud to have the support of leaders across our district who know what it takes to get things done. Their trust means everything, and I&rsquo;ll work to earn it every day.
        </>
      ),
    },
    {
      channel: "Facebook",
      eyebrow: "Draft · Facebook",
      body: (
        <>
          Join us Saturday, 3 to 7 PM, for a neighborhood Meet &amp; Greet at the community center. Stop by, ask questions, and hear our plan. Bring a neighbor.
        </>
      ),
    },
    {
      channel: "Facebook",
      eyebrow: "Draft · Facebook",
      body: (
        <>
          Some politicians promise lower taxes at election time and forget by the next budget. I&rsquo;ve spent my career making the numbers work. Real results, not just promises.
        </>
      ),
    },
  ];
  const [idx, setIdx] = useState(0);
  // Auto-rotate every 6s. Resets whenever the user clicks a dot (re-runs the effect).
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [idx, slides.length]);
  const slide = slides[idx];
  return (
    <div className="relative bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8" data-testid="hero-social-preview">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-testid="hero-social-eyebrow">{slide.eyebrow}</div>
        <div className="flex items-center gap-1.5" role="tablist" aria-label="Sample post examples">
          {slides.map((s, i) => (
            <button
              key={`slide-dot-${s.channel}-${i}`}
              type="button"
              role="tab"
              aria-selected={i === idx}
              aria-label={`Show example ${i + 1}`}
              onClick={() => setIdx(i)}
              data-testid={`hero-social-dot-${i}`}
              className={`h-2.5 w-2.5 rounded-full transition-colors ${i === idx ? "bg-flag-red" : "bg-slate-300 hover:bg-slate-400"}`}
            />
          ))}
        </div>
      </div>
      <p className="text-slate-800 leading-relaxed min-h-[7.5rem]" data-testid="hero-social-body">
        {slide.body}
      </p>
      <div className="mt-5 flex gap-2">
        {["Facebook", "X", "Instagram"].map((ch) => (
          <span
            key={ch}
            className={`text-xs px-2 py-1 rounded-md font-semibold transition-colors ${
              ch === slide.channel ? "bg-flag-red text-white" : "bg-slate-100 text-slate-700"
            }`}
          >
            {ch}
          </span>
        ))}
      </div>
    </div>
  );
}

function RoleCard({ icon: Icon, title, tagline, accent, bullets, ctaLabel, ctaTo, testid }) {
  const isRed = accent === "red";
  const iconCls = isRed ? "bg-flag-red text-white" : "bg-flag-navy text-white";
  const ctaCls = isRed ? "bg-flag-red hover:bg-flag-red-hover" : "bg-flag-navy hover:bg-flag-navy-2";
  return (
    <div
      data-testid={testid}
      className="bg-white border-2 border-slate-200 rounded-xl shadow-sm p-6 sm:p-7 flex flex-col hover:border-flag-red/40 hover:-translate-y-0.5 transition-all"
    >
      <span className={`inline-flex h-12 w-12 items-center justify-center rounded-lg ${iconCls}`}>
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="mt-4 font-display text-xl font-black text-flag-navy">{title}</h3>
      <p className="mt-1 text-flag-red font-semibold text-sm">{tagline}</p>
      <ul className="mt-4 space-y-1.5 text-sm text-slate-700 flex-1">
        {bullets.map((b, i) => (
          <li key={`b-${i}-${b}`} className="flex items-start gap-2">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-flag-red flex-shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <Link to={ctaTo} className="mt-5">
        <Button className={`w-full h-11 ${ctaCls} text-white font-bold`}>{ctaLabel}</Button>
      </Link>
    </div>
  );
}

